import { createStyles, makeStyles, Theme } from "@material-ui/core";
import RootRef from "@material-ui/core/RootRef";
import PublishIcon from "@material-ui/icons/Publish";
import Colors from "Constants/Colors";
import ElementSizes from "Constants/ElementSizes";
import ValidationConstants from "Constants/ValidationConstants";
import React, { FC, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

interface Props {
  handleFilesSelected(file: File[]): void;
  allowMultipleFiles: boolean;
  cpoFileDropZone: boolean;
  acceptedFileTypes: string[];
  dropzoneDisabledBool: boolean;
  children?:
    | React.ReactNode
    | ((openFileBrowser: () => void) => React.ReactNode);
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dropzone: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: theme.spacing(2),
      borderWidth: ElementSizes.FILE_UPLOAD_BORDER_WIDTH,
      borderRadius: ElementSizes.FILE_UPLOAD_BORDER_RADIUS,
      borderColor: Colors.FILE_UPLOAD_BORDER,
      borderStyle: "dashed",
      backgroundColor: Colors.FILE_UPLOAD_BACKGROUND,
      color: Colors.FILE_UPLOAD_TEXT,
    },
    dropzoneArea: {
      height: "100%",
      alignItems: "stretch",
      flexDirection: "column",
      flex: 1,
    },

    errorMessage: {
      color: theme.palette.error.main,
    },
  }),
);

export const FileDropZone: FC<Props> = ({
  handleFilesSelected: handleFileSelected,
  allowMultipleFiles,
  cpoFileDropZone,
  acceptedFileTypes,
  dropzoneDisabledBool,
  children,
}) => {
  const classes = useStyles();
  const { t } = useTranslation("files");

  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileTypeError, setFileTypeError] = useState(false);

  const { acceptedFiles, getRootProps, getInputProps, isDragActive, open } =
    useDropzone({
      noClick: !!children,
      noKeyboard: !!children,
      onDropAccepted: (files) => {
        setFileSizeError(false);
        setFileTypeError(false);
        onFileDrop(files);
      },
      disabled: dropzoneDisabledBool,
      onDropRejected: (files) => {
        files.forEach((file) => {
          if (file.size > ValidationConstants.MAX_FILE_SIZE) {
            setFileSizeError(true);
            setFileTypeError(false);
          } else {
            setFileSizeError(false);
            setFileTypeError(true);
          }
        });
      },
      multiple: allowMultipleFiles,
      accept: acceptedFileTypes,
      maxSize: ValidationConstants.MAX_FILE_SIZE,
    });
  const { ref, ...rootProps } = getRootProps();

  const onFileDrop = (files: File[]) => {
    handleFileSelected(files);
  };

  return (
    <div className={classes.dropzoneArea}>
      <RootRef rootRef={ref}>
        <div
          {...rootProps}
          className={classes.dropzone}
          style={
            children
              ? {
                  position: "relative",
                  height: "100%",
                  outline: "none",
                  display: "block",
                  padding: 0,
                  borderStyle: "none",
                }
              : undefined
          }
        >
          <input {...getInputProps()} />

          {children ? (
            <>
              {typeof children === "function"
                ? (children as (open: () => void) => React.ReactNode)(open)
                : children}
              {isDragActive && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    border: "2px dashed #0064fa",
                    zIndex: 9999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    color: "#0064fa",
                  }}
                >
                  <PublishIcon style={{ fontSize: 60, marginBottom: 16 }} />
                  {cpoFileDropZone ? t("cpoFileHint") : t("hint")}
                </div>
              )}
            </>
          ) : (
            <>
              {cpoFileDropZone ? (
                <div>{t("cpoFileHint")}</div>
              ) : (
                <div>{t("hint")}</div>
              )}
              <PublishIcon />
            </>
          )}
        </div>
      </RootRef>

      {fileSizeError && (
        <div className={classes.errorMessage}>{t("errorFileSize")}</div>
      )}
      {fileTypeError && (
        <div className={classes.errorMessage}>{t("errorFileType")}</div>
      )}

      {!cpoFileDropZone &&
        acceptedFiles.map((file) => (
          <div key={file.name}>
            {file.name} -{" "}
            {file.size > ValidationConstants.MEGABYTE_IN_BYTES
              ? `${(
                  file.size * ValidationConstants.BYTES_TO_MEGABYTES_FACTOR
                ).toFixed(2)} MB`
              : `${(
                  file.size * ValidationConstants.BYTES_TO_KILOBYTES_FACTOR
                ).toFixed(2)} KB`}
          </div>
        ))}
    </div>
  );
};
